import React from "react";
import {Badge, PageHeading, PageHeadingMeta, PageHeadingMetaItem} from "peggirkit";
import {FormattedMessage} from "react-intl";
import {OptionGroup, ProductOption} from "@/data/entities/option";
import {OptionGroupsLookup} from "@widgets/menu/optionGroups/optionGroupsLookup";
import OptionsTable from "@widgets/menu/optionGroups/group/OptionsTable";

type Props = {
    group: OptionGroup,
    lookup: OptionGroupsLookup,
    disabled: boolean,
    onRemoveOption: (option: ProductOption, otherGroupCount: number) => void,
};

const OptionGroupTypeBadge = ({group}: { group: OptionGroup }) => (
    <Badge
        size={"xs"}
        color={group.type === "required" ? "primary" : "secondary"}
    >
        {
            group.type === "required"
                ? <FormattedMessage id={"optionGroupType.required"}/>
                : group.maxOptions === 0
                    ? <FormattedMessage id={"optionGroupType.optionalUnlimited"}/>
                    : <FormattedMessage id={"optionGroupType.optional"} values={{max: group.maxOptions}}/>
        }
    </Badge>
);

const OptionGroupSection = ({group, lookup, disabled, onRemoveOption}: Props) => {
    const options: ProductOption[] = group.optionIds
        .map(optionId => lookup.options.get(optionId))
        .filter((option): option is ProductOption => option !== undefined);

    return (
        <li
            data-option-group-id={group.id}
            className={"flex flex-col gap-4 w-full scroll-mt-6"}
        >
            <PageHeading
                titleSize={"sm"}
                title={group.name}
                meta={
                    <PageHeadingMeta>
                        <PageHeadingMetaItem>
                            <OptionGroupTypeBadge group={group}/>
                        </PageHeadingMetaItem>
                        <PageHeadingMetaItem>
                            <span className={"text-sm text-gray-500 dark:text-slate-400"}>
                                <FormattedMessage
                                    id={"usedByProducts"}
                                    values={{count: lookup.productCountByGroup.get(group.id) ?? 0}}
                                />
                            </span>
                        </PageHeadingMetaItem>
                    </PageHeadingMeta>
                }
            />

            <OptionsTable
                options={options}
                lookup={lookup}
                disabled={disabled}
                onRemoveOption={onRemoveOption}
            />
        </li>
    );
};

export default OptionGroupSection;